import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import Sketch from '@arcgis/core/widgets/Sketch';
import FulcrumSnappingOptions from './fulcrum-snapping-options';
import { GeometricType } from '../types/geo';
import { supportedGeometryTypes } from './supported-geometry-types';

export const SKETCH_LAYER_ID = 'fulcrum-sketch-layer';
export const VIEW_LAYER_ID = 'fulcrum-view-layer';
export const STATIC_LAYER_ID = 'fulcrum-static-layer';
export const SKETCH_LAYER_TITLE = 'Sketch Layer';
export const VIEW_LAYER_TITLE = 'View Only Layer';
export const STATIC_LAYER_TITLE = 'Nearby Records';

export interface Providable {
  sketch: () => Sketch;
  sketchLayer: () => GraphicsLayer;
  viewLayer: () => GraphicsLayer;
  staticLayer: () => GraphicsLayer;
}

export const orderedLayerNames = () => [
  STATIC_LAYER_ID, VIEW_LAYER_ID, SKETCH_LAYER_ID,
];

const Providers = (mapView: MapView, existingSketch?: Sketch) => {
  const types: GeometricType[] = supportedGeometryTypes(mapView);

  const newLayer = (id: string, title: string) => {
    const graphicsLayer = new GraphicsLayer({ id, title });
    mapView.map?.add(graphicsLayer);
    return graphicsLayer;
  };

  const existingLayer = (id: string) => (mapView.map?.findLayerById(id));

  const sketchLayer = () => (existingLayer(SKETCH_LAYER_ID) || newLayer(SKETCH_LAYER_ID, SKETCH_LAYER_TITLE));
  const viewLayer = () => (existingLayer(VIEW_LAYER_ID) || newLayer(VIEW_LAYER_ID, VIEW_LAYER_TITLE));
  const staticLayer = () => (existingLayer(STATIC_LAYER_ID) || newLayer(STATIC_LAYER_ID, STATIC_LAYER_TITLE));

  const tools = () => ({
    point: types.includes('Point'),
    polyline: types.includes('LineString'),
    polygon: types.includes('Polygon'),
    rectangle: false,
    circle: false,
  });

  const sketch = (): Sketch => {
    if (existingSketch) {
      return existingSketch;
    }

    const snappingOptions = FulcrumSnappingOptions(mapView);
    const layer = sketchLayer();

    // Initialize other layers
    viewLayer();
    staticLayer();
    return new Sketch({
      layer,
      view: mapView,
      defaultUpdateOptions: {
        multipleSelectionEnabled: false,
      },
      tooltipOptions: {
        enabled: true,
      },
      creationMode: 'single',
      visible: false,
      snappingOptions: snappingOptions || { enabled: false },
      visibleElements: {
        createTools: tools(),
        selectionTools: {
          'lasso-selection': false,
          'rectangle-selection': false,
        },
        settingsMenu: true,
        snappingControls: !!snappingOptions,
        // @ts-ignore
        duplicateButton: false,
      },
    });
  };

  return {
    sketch,
    sketchLayer,
    viewLayer,
    staticLayer,
  };
};

export default Providers;
